import React, { useState } from "react";
import { Link } from "react-router-dom";
import AuthDialog from "../AuthDialog";
import { AGENTS } from "../../data/agents";
import AgentIcon from "../AgentIcon";
import {
  ArrowRight, Sparkles, Layers, Users, Compass, ShieldCheck, FileText, Network,
  CheckCircle2, BadgeCheck, Lock, Eye, AlertTriangle, Workflow, MoveRight, Star,
} from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/c95a9b01-5560-4706-85a1-f7619a177f38/images/de6fad31b34ce4b8562d21401a583f53410bc3683d44f7e3e659059fd289240e.png";
const DELPHI_HERO = "https://static.prod-images.emergentagent.com/jobs/c95a9b01-5560-4706-85a1-f7619a177f38/images/6fd8d369a5799244462bc3efb11f1b77aa02bfe7126d23b0f65b722af0f4c6b1.png";
const PROFESSIONALS = "https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBtZWV0aW5nfGVufDB8fHx8MTc3NzEwNTI3Nnww&ixlib=rb-4.1.0&q=85";
const NODES = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwbm9kZXN8ZW58MHx8fHwxNzc3MTA1Mjc2fDA&ixlib=rb-4.1.0&q=85";

const Container = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 ${className}`}>{children}</div>
);

const Overline = ({ children }) => (
  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0EA5E9]">{children}</p>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-24 sm:py-32 ${className}`}>{children}</section>
);

const PrimaryBtn = ({ children, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="inline-flex items-center gap-2 bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-6 py-3 rounded-lg font-bold transition-colors font-display"
  >
    {children}
  </button>
);

const SecondaryBtn = ({ children, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="inline-flex items-center gap-2 bg-white text-[#0B1B3D] border-2 border-[#0B1B3D] hover:bg-slate-50 px-6 py-3 rounded-lg font-bold transition-colors font-display"
  >
    {children}
  </button>
);

const Header = ({ openAuth }) => (
  <header className="backdrop-blur-xl bg-white/80 border-b border-slate-200 sticky top-0 z-50">
    <Container className="flex items-center justify-between h-16">
      <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
        <div className="w-8 h-8 rounded-md bg-[#0B1B3D] grid place-items-center">
          <span className="font-display font-black text-white">K</span>
        </div>
        <span className="font-display font-extrabold text-[#0B1B3D] text-lg tracking-tight">Konzenzo</span>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#475569]">
        <a href="#how" className="hover:text-[#0B1B3D]">How it works</a>
        <a href="#agents" className="hover:text-[#0B1B3D]">Expert Agents</a>
        <a href="#method" className="hover:text-[#0B1B3D]">Konzenzo method</a>
        <a href="#marketplace" className="hover:text-[#0B1B3D]">Marketplace</a>
        <a href="#pricing" className="hover:text-[#0B1B3D]">Pricing</a>
      </nav>
      <div className="flex items-center gap-3">
        <button onClick={() => openAuth("signin")} className="text-sm font-bold text-[#0B1B3D] hover:underline" data-testid="header-signin-btn">Sign in</button>
        <PrimaryBtn onClick={() => openAuth("signup")} testid="header-cta">
          Start a session <ArrowRight className="w-4 h-4" />
        </PrimaryBtn>
      </div>
    </Container>
  </header>
);

const Hero = ({ openAuth }) => (
  <section className="relative overflow-hidden border-b border-slate-200 kz-noise">
    <div className="absolute inset-0 kz-grid-bg opacity-60 pointer-events-none" />
    <Container className="relative pt-20 pb-24 sm:pt-28 sm:pb-32">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 kz-rise">
          <Overline>Konzenzo. Where experts align.</Overline>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black text-[#0B1B3D] leading-[1.02] tracking-tight">
            Make better business decisions <span className="text-[#0EA5E9]">with AI expert panels.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[#475569] leading-relaxed max-w-2xl">
            Konzenzo helps small and mid-sized businesses analyze complex decisions using
            multiple AI agents, structured expert reasoning and Konzenzo-method consensus —
            so you stop guessing and start aligning.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <PrimaryBtn onClick={() => openAuth("signup")} testid="hero-primary-cta">Start a decision session <ArrowRight className="w-4 h-4" /></PrimaryBtn>
            <a href="#agents">
              <SecondaryBtn testid="hero-secondary-cta">Explore expert agents</SecondaryBtn>
            </a>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#475569]">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> 10 specialized AI experts</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> 3-round Konzenzo consensus</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Boardroom-ready reports</div>
          </div>
        </div>
        <div className="lg:col-span-5 kz-rise kz-d2">
          <div className="relative rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
            <img src={HERO_BG} alt="AI consensus" className="w-full h-72 object-cover" />
            <div className="p-6 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#0EA5E9]">Live decision panel</span>
                <span className="text-xs font-mono-kz text-[#94A3B8]">Round 3/3</span>
              </div>
              <p className="mt-3 font-display font-extrabold text-[#0B1B3D] text-lg">How can we reduce VAT impact in our bakery?</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-md bg-[#F8FAFC] border border-slate-200 p-3">
                  <p className="font-mono-kz text-xs text-[#94A3B8]">CFO</p>
                  <p className="font-bold text-[#0B1B3D]">Confidence 86%</p>
                </div>
                <div className="rounded-md bg-[#F8FAFC] border border-slate-200 p-3">
                  <p className="font-mono-kz text-xs text-[#94A3B8]">Tax Advisor</p>
                  <p className="font-bold text-[#0B1B3D]">Confidence 92%</p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-[#10B981] font-bold"><BadgeCheck className="w-4 h-4" /> Consensus reached</div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

const ProblemSolution = () => (
  <Section className="bg-[#F8FAFC] border-b border-slate-200">
    <Container>
      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <Overline>The problem</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-[#0B1B3D] tracking-tight leading-tight">SMEs make critical decisions without expert panels.</h2>
          <p className="mt-6 text-[#475569] text-lg leading-relaxed">
            Most small and mid-sized businesses face complex decisions — taxes, pricing, vendors,
            hiring — with incomplete information, no time, and no access to specialized experts.
            Big consulting firms are out of reach. Generic AI chat is not enough.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[
              { k: "76%", v: "of SMEs report regretting major decisions made without expert input." },
              { k: "$120K+", v: "average annual cost of dedicated boutique consulting." },
              { k: "1 voice", v: "is what generic AI gives you. Real decisions need many." },
              { k: "Weeks", v: "lost gathering input from disconnected advisors." },
            ].map((s) => (
              <div key={s.k} className="bg-white border border-slate-200 rounded-xl p-5">
                <p className="font-display text-2xl font-black text-[#0B1B3D]">{s.k}</p>
                <p className="text-sm text-[#475569] mt-1">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-7 md:pl-8">
          <Overline>The Konzenzo way</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-[#0B1B3D] tracking-tight leading-tight">A panel of AI experts that actually argue, refine and align.</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Users, t: "Multi-agent panels", d: "Pick CFO, Tax, Legal, Ops, Pricing, Compliance and more." },
              { icon: Workflow, t: "Konzenzo rounds", d: "Independent views first, then refinement, then consensus." },
              { icon: Eye, t: "Transparent reasoning", d: "See agreement, disagreement, risks and assumptions." },
              { icon: FileText, t: "Action-ready reports", d: "Executive summary, action plan, exportable PDF." },
            ].map((s) => (
              <div key={s.t} className="bg-white border border-slate-200 rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
                <s.icon className="w-6 h-6 text-[#0EA5E9]" />
                <p className="mt-4 font-display font-bold text-[#0B1B3D] text-lg">{s.t}</p>
                <p className="text-sm text-[#475569] mt-2">{s.d}</p>
              </div>
            ))}
          </div>
          <img src={PROFESSIONALS} alt="advisors" className="mt-8 w-full h-48 object-cover rounded-xl border border-slate-200" />
        </div>
      </div>
    </Container>
  </Section>
);

const HowItWorks = () => {
  const steps = [
    { t: "Describe your business challenge", d: "Title, context, jurisdiction, urgency, desired outcome." },
    { t: "Select expert AI agents", d: "Use a recommended panel or hand-pick your experts." },
    { t: "Run a Konzenzo-style session", d: "Independent views → cross-expert refinement → final round." },
    { t: "Compare expert perspectives", d: "Areas of agreement, disagreement, confidence and risks." },
    { t: "Receive a consolidated decision report", d: "Executive summary, action plan, open questions, PDF export." },
  ];
  return (
    <Section id="how">
      <Container>
        <div className="max-w-3xl">
          <Overline>How it works</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-[#0B1B3D] tracking-tight leading-tight">Five steps from question to confident decision.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <div key={s.t} className="relative bg-white border border-slate-200 rounded-xl p-6">
              <div className="font-mono-kz text-xs text-[#0EA5E9] font-bold tracking-widest">STEP 0{i + 1}</div>
              <p className="mt-4 font-display font-bold text-[#0B1B3D] text-lg leading-snug">{s.t}</p>
              <p className="mt-3 text-sm text-[#475569]">{s.d}</p>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
};

const UseCases = () => {
  const cases = [
    "Tax optimization for small businesses", "Cash flow & working capital", "Pricing strategy",
    "Vendor selection", "Process improvement", "SAP / process transformation",
    "Hiring & organizational design", "Market entry evaluation", "Risk & compliance",
  ];
  return (
    <Section className="bg-[#F8FAFC] border-y border-slate-200">
      <Container>
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <Overline>Use cases</Overline>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-[#0B1B3D] tracking-tight leading-tight">Built for the decisions SMEs actually face.</h2>
          </div>
          <p className="md:col-span-5 text-[#475569]">From tax planning at a bakery to S/4HANA OTC reviews at a manufacturer, Konzenzo handles the full spectrum.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cases.map((c) => (
            <div key={c} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-3 hover:border-[#0EA5E9] transition-colors">
              <Sparkles className="w-4 h-4 text-[#10B981]" />
              <span className="font-display font-bold text-[#0B1B3D]">{c}</span>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
};

const ExpertAgents = () => (
  <Section id="agents">
    <Container>
      <div className="max-w-3xl">
        <Overline>Expert agents</Overline>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-[#0B1B3D] tracking-tight leading-tight">A senior panel, on demand.</h2>
        <p className="mt-6 text-[#475569] text-lg">Each agent has a defined persona, scope and analysis style. Mix and match to fit your decision.</p>
      </div>
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {AGENTS.map((a) => (
          <div key={a.id} className="flex flex-col gap-4 p-6 bg-white border border-slate-200 rounded-xl hover:border-[#0EA5E9] hover:shadow-md transition-all group" data-testid={`agent-card-${a.id}`}>
            <div className="w-10 h-10 rounded-md bg-[#0B1B3D] grid place-items-center text-white">
              <AgentIcon name={a.icon} className="w-5 h-5" />
            </div>
            <p className="font-display font-extrabold text-[#0B1B3D] text-lg leading-tight">{a.name}</p>
            <p className="text-sm text-[#0EA5E9] font-bold uppercase tracking-wider font-mono-kz">{a.specialty}</p>
            <div className="text-sm text-[#475569] space-y-2">
              <p><span className="font-bold text-[#0B1B3D]">When to use: </span>{a.when_to_use}</p>
              <p><span className="font-bold text-[#0B1B3D]">Analysis: </span>{a.analysis_type}</p>
            </div>
          </div>
        ))}
      </div>
    </Container>
  </Section>
);

const DelphiSection = () => (
  <Section id="method" className="bg-[#0B1B3D] text-white relative overflow-hidden">
    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${DELPHI_HERO})`, backgroundSize: "cover", backgroundPosition: "center" }} />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3D]/70 to-[#0B1B3D]" />
    <Container className="relative">
      <div className="max-w-3xl">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#10B981]">The Konzenzo method</p>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">Many experts. Structured rounds. One aligned decision.</h2>
        <p className="mt-6 text-slate-300 text-lg">
          Instead of relying on one opinion, Konzenzo gathers structured perspectives from multiple expert agents,
          compares their reasoning, identifies consensus and disagreement, and helps you make a better-informed decision.
        </p>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {[
          { r: "Round 1", t: "Independent views", d: "Each agent analyzes the challenge in isolation, surfacing assumptions, risks and a first recommendation." },
          { r: "Round 2", t: "Cross-expert refinement", d: "Agents read peers' opinions, react to disagreements and refine their own reasoning." },
          { r: "Round 3", t: "Consensus & decision", d: "A final decisive round produces consolidated recommendations, risks and an action plan." },
        ].map((s) => (
          <div key={s.r} className="rounded-xl border border-white/15 bg-white/5 backdrop-blur-md p-6">
            <p className="font-mono-kz text-xs text-[#10B981] tracking-widest">{s.r}</p>
            <p className="mt-3 font-display text-xl font-extrabold">{s.t}</p>
            <p className="mt-3 text-slate-300 text-sm">{s.d}</p>
          </div>
        ))}
      </div>
    </Container>
  </Section>
);

const Marketplace = () => (
  <Section id="marketplace">
    <Container>
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#10B981]/10 text-[#10B981] font-bold text-xs px-3 py-1 font-mono-kz tracking-wider uppercase"><Star className="w-3 h-3" /> Coming soon</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl font-extrabold text-[#0B1B3D] tracking-tight leading-tight">Expert Marketplace</h2>
          <p className="mt-6 text-[#475569] text-lg">Buy specialized agent packs by industry, hire human-certified expert reviews, and combine AI panels with consultants you trust.</p>
          <ul className="mt-6 space-y-3 text-[#475569]">
            <li className="flex gap-3"><MoveRight className="w-4 h-4 text-[#0EA5E9] mt-1" />Industry-specific AI agents and packs</li>
            <li className="flex gap-3"><MoveRight className="w-4 h-4 text-[#0EA5E9] mt-1" />Human expert review on regulated topics</li>
            <li className="flex gap-3"><MoveRight className="w-4 h-4 text-[#0EA5E9] mt-1" />Publish needs, receive analysis proposals</li>
          </ul>
        </div>
        <div className="md:col-span-7 relative rounded-2xl overflow-hidden border border-slate-200">
          <img src={NODES} alt="marketplace" className="w-full h-[420px] object-cover" />
          <div className="absolute inset-0 backdrop-blur-md bg-white/40" />
          <div className="absolute inset-0 p-8 grid sm:grid-cols-2 gap-4">
            {[
              "Bakery Tax Optimization Pack", "SME CFO Review Pack", "SAP S/4HANA OTC Review",
              "Working Capital Diagnostic", "Vendor Transition Assessment", "Pricing Strategy Review",
            ].map((p, i) => (
              <div key={p} className="rounded-xl border border-white/60 bg-white/85 backdrop-blur-xl p-5 shadow-sm">
                <p className="font-display font-extrabold text-[#0B1B3D]">{p}</p>
                <p className="text-xs font-mono-kz text-[#94A3B8] mt-1 tracking-widest">PACK · COMING SOON</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Container>
  </Section>
);

const Trust = () => (
  <Section className="bg-[#F8FAFC] border-y border-slate-200">
    <Container>
      <div className="max-w-3xl">
        <Overline>Trust & security</Overline>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-[#0B1B3D] tracking-tight leading-tight">Built for confidential business decisions.</h2>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { i: Lock, t: "Confidential by default", d: "Sessions are scoped to your account; data never trains shared models." },
          { i: ShieldCheck, t: "Clear audit trail", d: "Every agent reasoning step is recorded and reviewable." },
          { i: Eye, t: "Decision logic transparency", d: "See assumptions, confidence and disagreement explicitly." },
          { i: Network, t: "No generic answers", d: "Each agent has a defined scope, persona and analysis method." },
          { i: AlertTriangle, t: "Risk-aware", d: "Compliance and Critic agents flag what to validate locally." },
          { i: Users, t: "Optional human review", d: "Coming soon: certified human experts can review reports." },
        ].map((s) => (
          <div key={s.t} className="bg-white border border-slate-200 rounded-xl p-6">
            <s.i className="w-6 h-6 text-[#0EA5E9]" />
            <p className="mt-4 font-display font-extrabold text-[#0B1B3D] text-lg">{s.t}</p>
            <p className="mt-2 text-sm text-[#475569]">{s.d}</p>
          </div>
        ))}
      </div>
    </Container>
  </Section>
);

const Pricing = ({ openAuth }) => {
  const tiers = [
    { name: "Free", price: "€0", per: "/mo", desc: "Try Konzenzo with 100 credits.", features: ["1 user", "100 credits / month", "Basic agents only", "Watermarked report"], cta: "Start free", highlight: false },
    { name: "Starter", price: "€39", per: "/mo", desc: "For solo founders & freelancers.", features: ["2 users", "150 credits / month", "Up to 5 agents", "PDF export"], cta: "Choose Starter", highlight: false },
    { name: "Professional", price: "€129", per: "/mo", desc: "For consultants & small teams.", features: ["5 users", "500 credits / month", "All 10 agents + premium", "Marketplace + consultants"], cta: "Choose Professional", highlight: true },
    { name: "Business", price: "€399", per: "/mo", desc: "For growing SMEs.", features: ["15 users", "2,000 credits / month", "Full agent library", "Admin controls"], cta: "Choose Business", highlight: false },
    { name: "Enterprise", price: "Custom", desc: "Custom credits, SSO, audit logs, SLAs.", features: ["Custom users & credits", "SSO + RBAC", "Audit logs", "Dedicated success"], cta: "Talk to us", highlight: false },
  ];
  const packs = [
    { n: "100 credits", p: "€15",  hint: "€0.15/cr — light extra" },
    { n: "500 credits", p: "€65",  hint: "€0.13/cr — regular" },
    { n: "1,000 credits", p: "€120", hint: "€0.12/cr — teams" },
    { n: "5,000 credits", p: "€500", hint: "€0.10/cr — heavy users" },
  ];
  const faq = [
    { q: "What is a credit?", a: "A credit is the internal unit Konzenzo uses to measure feature usage. A basic 1-agent session = 10 credits, a 2-3 agent session = 25 credits, an advanced multi-agent session = 50 credits, plus 20 credits per executive report." },
    { q: "Do unused credits roll over?", a: "Monthly plan credits reset every billing cycle. Purchased credit packs are valid for 12 months and only consumed after monthly credits run out." },
    { q: "What happens if a session fails?", a: "Failed Konzenzo sessions automatically refund your credits to the workspace wallet — no payment for broken outputs." },
    { q: "Are credits per user or per workspace?", a: "Per workspace. Everyone in your team draws from the same credit pool, with optional per-user spending limits coming in the Business plan." },
    { q: "Is VAT included?", a: "Prices are net of VAT. EU customers see the applicable VAT calculated at checkout via Stripe Tax." },
  ];
  return (
    <Section id="pricing">
      <Container>
        <div className="max-w-3xl">
          <Overline>Pricing</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-[#0B1B3D] tracking-tight leading-tight">Plans that scale with your decisions.</h2>
          <p className="mt-6 text-[#475569] text-lg">Subscription credits + optional credit packs. Pay only for the analyses you run.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {tiers.map((t) => (
            <div key={t.name} className={`relative rounded-xl bg-white p-6 border ${t.highlight ? "border-[#10B981]" : "border-slate-200"}`}>
              {t.highlight && <span className="absolute -top-3 left-6 bg-[#10B981] text-white text-xs font-bold px-3 py-1 rounded-full font-mono-kz tracking-widest">MOST POPULAR</span>}
              <p className="font-display font-extrabold text-[#0B1B3D] text-xl">{t.name}</p>
              <p className="mt-3 text-sm text-[#475569]">{t.desc}</p>
              <p className="mt-5 font-display text-3xl font-black text-[#0B1B3D]">{t.price}<span className="text-base text-[#94A3B8] font-mono-kz">{t.per || ""}</span></p>
              <ul className="mt-6 space-y-2 text-sm text-[#475569]">
                {t.features.map((f) => <li key={f} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" />{f}</li>)}
              </ul>
              <button onClick={() => openAuth("signup")} data-testid={`pricing-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`} className={`mt-7 w-full px-5 py-3 rounded-lg font-bold transition-colors ${t.highlight ? "bg-[#10B981] text-white hover:bg-[#059669]" : "bg-[#0B1B3D] text-white hover:bg-[#1E3A8A]"} font-display`}>{t.cta}</button>
            </div>
          ))}
        </div>

        <div className="mt-20 max-w-3xl">
          <Overline>Credit packs</Overline>
          <h3 className="mt-4 font-display text-3xl font-extrabold text-[#0B1B3D] tracking-tight">Need a top-up? Pay-as-you-go.</h3>
          <p className="mt-3 text-[#475569]">Buy extra credits anytime. Valid for 12 months. Used only after your monthly credits.</p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {packs.map((p) => (
            <div key={p.n} className="border border-slate-200 rounded-xl p-5 bg-white">
              <p className="font-display font-extrabold text-[#0B1B3D] text-lg">{p.n}</p>
              <p className="mt-2 font-display text-3xl font-black text-[#0B1B3D]">{p.p}</p>
              <p className="mt-1 text-xs font-mono-kz text-[#94A3B8] tracking-widest uppercase">{p.hint}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 max-w-3xl">
          <Overline>FAQ</Overline>
          <h3 className="mt-4 font-display text-3xl font-extrabold text-[#0B1B3D] tracking-tight">How credits work.</h3>
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {faq.map((f) => (
            <div key={f.q} className="border border-slate-200 rounded-xl p-5 bg-white">
              <p className="font-display font-bold text-[#0B1B3D]">{f.q}</p>
              <p className="mt-2 text-sm text-[#475569] leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-xs text-[#94A3B8] font-mono-kz max-w-3xl">Konzenzo provides AI-assisted analysis to support — not replace — qualified human review for tax, legal, and financial matters. Final decisions remain the sole responsibility of the user or organization.</p>
      </Container>
    </Section>
  );
};

const FinalCTA = ({ openAuth }) => (
  <section className="relative bg-[#0B1B3D] text-white py-24 overflow-hidden">
    <div className="absolute inset-0 kz-grid-bg opacity-10" />
    <Container className="relative text-center max-w-3xl">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#10B981]">Ready to decide?</p>
      <h2 className="mt-6 font-display text-4xl sm:text-6xl font-black tracking-tight leading-[1.05]">Create your first expert decision panel.</h2>
      <p className="mt-6 text-slate-300 text-lg">Sign in with Google or email. Run your first Konzenzo session in minutes.</p>
      <div className="mt-10 flex justify-center gap-4">
        <button onClick={() => openAuth("signup")} data-testid="final-cta-btn" className="bg-[#10B981] hover:bg-[#059669] text-white font-bold px-7 py-4 rounded-lg font-display inline-flex items-center gap-2">Start a session <ArrowRight className="w-4 h-4" /></button>
      </div>
    </Container>
  </section>
);

const Footer = () => (
  <footer className="border-t border-slate-200 bg-white">
    <Container className="py-10 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-[#0B1B3D] grid place-items-center"><span className="font-display font-black text-white text-sm">K</span></div>
        <span className="font-display font-extrabold text-[#0B1B3D]">Konzenzo</span>
        <span className="text-sm text-[#94A3B8] font-mono-kz">— Where experts align.</span>
      </div>
      <p className="text-sm text-[#94A3B8]">© {new Date().getFullYear()} Konzenzo. All rights reserved.</p>
    </Container>
  </footer>
);

export default function Landing() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const openAuth = (mode = "signin") => { setAuthMode(mode); setAuthOpen(true); };
  return (
    <div className="bg-white text-[#0B1B3D]" data-testid="landing-page">
      <Header openAuth={openAuth} />
      <Hero openAuth={openAuth} />
      <ProblemSolution />
      <HowItWorks />
      <UseCases />
      <ExpertAgents />
      <DelphiSection />
      <Marketplace />
      <Trust />
      <Pricing openAuth={openAuth} />
      <FinalCTA openAuth={openAuth} />
      <Footer />
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />
    </div>
  );
}
